import React from 'react';
import styled, { keyframes } from 'styled-components';

// Outer element rises from bottom; inner element sways side to side.
// Two-layer technique gives organic, non-linear movement.

const floatUp = keyframes`
  0%   { bottom: -100px; opacity: 0; }
  6%   { opacity: 0.35; }
  94%  { opacity: 0.22; }
  100% { bottom: 112vh;  opacity: 0; }
`;

const sway = keyframes`
  0%, 100% { transform: translateX(0px); }
  28%       { transform: translateX(38px); }
  72%       { transform: translateX(-28px); }
`;

const Wrap = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
`;

const BubbleOuter = styled.span`
  position: absolute;
  bottom: -100px;
  display: block;
  animation: ${floatUp} linear infinite;
`;

const BubbleInner = styled.span`
  display: block;
  border-radius: 50%;
  background: rgba(245, 136, 42, 0.35);
  animation: ${sway} ease-in-out infinite;
`;

// Deterministic bubble data — no random per-render, smooth staggered coverage
const BUBBLES = [
  { id: 1,  left: '3%',  size: '18px', dur: '18s', delay: '-2s',  sway: '6s',  swayD: '0s'  },
  { id: 2,  left: '8%',  size: '46px', dur: '24s', delay: '-10s', sway: '8s',  swayD: '-3s' },
  { id: 3,  left: '15%', size: '22px', dur: '16s', delay: '-5s',  sway: '5s',  swayD: '-1s' },
  { id: 4,  left: '20%', size: '64px', dur: '28s', delay: '-18s', sway: '9s',  swayD: '-4s' },
  { id: 5,  left: '27%', size: '14px', dur: '14s', delay: '-8s',  sway: '4s',  swayD: '-2s' },
  { id: 6,  left: '33%', size: '38px', dur: '22s', delay: '-14s', sway: '7s',  swayD: '0s'  },
  { id: 7,  left: '38%', size: '76px', dur: '30s', delay: '-3s',  sway: '10s', swayD: '-5s' },
  { id: 8,  left: '44%', size: '24px', dur: '17s', delay: '-11s', sway: '6s',  swayD: '-3s' },
  { id: 9,  left: '50%', size: '52px', dur: '25s', delay: '-7s',  sway: '8s',  swayD: '-1s' },
  { id: 10, left: '55%', size: '30px', dur: '19s', delay: '-16s', sway: '5s',  swayD: '-4s' },
  { id: 11, left: '61%', size: '70px', dur: '27s', delay: '-1s',  sway: '9s',  swayD: '0s'  },
  { id: 12, left: '67%', size: '20px', dur: '15s', delay: '-12s', sway: '4s',  swayD: '-2s' },
  { id: 13, left: '72%', size: '44px', dur: '21s', delay: '-20s', sway: '7s',  swayD: '-5s' },
  { id: 14, left: '78%', size: '34px', dur: '23s', delay: '-6s',  sway: '6s',  swayD: '-1s' },
  { id: 15, left: '84%', size: '58px', dur: '26s', delay: '-15s', sway: '8s',  swayD: '-3s' },
  { id: 16, left: '90%', size: '16px', dur: '13s', delay: '-9s',  sway: '5s',  swayD: '0s'  },
  { id: 17, left: '5%',  size: '40px', dur: '20s', delay: '-17s', sway: '7s',  swayD: '-4s' },
  { id: 18, left: '12%', size: '26px', dur: '18s', delay: '-4s',  sway: '6s',  swayD: '-2s' },
  { id: 19, left: '24%', size: '80px', dur: '32s', delay: '-13s', sway: '10s', swayD: '-5s' },
  { id: 20, left: '35%', size: '16px', dur: '14s', delay: '-19s', sway: '4s',  swayD: '0s'  },
  { id: 21, left: '47%', size: '48px', dur: '22s', delay: '-8s',  sway: '8s',  swayD: '-3s' },
  { id: 22, left: '58%', size: '28px', dur: '17s', delay: '-21s', sway: '5s',  swayD: '-1s' },
  { id: 23, left: '69%', size: '62px', dur: '29s', delay: '-11s', sway: '9s',  swayD: '-4s' },
  { id: 24, left: '76%', size: '12px', dur: '12s', delay: '-4s',  sway: '4s',  swayD: '-2s' },
  { id: 25, left: '88%', size: '36px', dur: '20s', delay: '-16s', sway: '6s',  swayD: '0s'  },
  { id: 26, left: '93%', size: '54px', dur: '24s', delay: '-2s',  sway: '8s',  swayD: '-5s' },
  { id: 27, left: '42%', size: '10px', dur: '11s', delay: '-7s',  sway: '4s',  swayD: '-3s' },
  { id: 28, left: '16%', size: '32px', dur: '19s', delay: '-23s', sway: '6s',  swayD: '-1s' },
  { id: 29, left: '63%', size: '42px', dur: '23s', delay: '-9s',  sway: '7s',  swayD: '-2s' },
  { id: 30, left: '96%', size: '22px', dur: '16s', delay: '-14s', sway: '5s',  swayD: '0s'  },
];

function FloatingBubbles() {
  return (
    <Wrap aria-hidden="true">
      {BUBBLES.map(b => (
        <BubbleOuter
          key={b.id}
          style={{
            left:              b.left,
            animationDuration: b.dur,
            animationDelay:    b.delay,
          }}
        >
          <BubbleInner
            style={{
              width:             b.size,
              height:            b.size,
              animationDuration: b.sway,
              animationDelay:    b.swayD,
            }}
          />
        </BubbleOuter>
      ))}
    </Wrap>
  );
}

export default FloatingBubbles;
